/* @flow */

import 'whatwg-fetch';
import { mapKeys, mapValues } from 'lodash';
import { pathJoin } from '../utils';

// import type { NewDesignResponseType } from '../actions/design';

type ApiResponseErrorType = {
  message: string,
  name: string,
  response: {},
  stack: string,
};

export function APIResponseError(message: string = '') {
  this.name = 'APIResponseError';
  this.message = message;
  this.stack = (new Error()).stack;
  this.response = {};
}

APIResponseError.prototype = Object.create(Error.prototype);
APIResponseError.prototype.constructor = APIResponseError;

// type ApiCallType<T> = () => {
//   error: APIResponseError,
//   response: T,
// };

type ResponseType = {
  json(): Promise<any>,
  status: number,
  statusText: string,
};

export function checkStatus(response: ResponseType): ResponseType {
  if (response.status >= 200 && response.status <= 400) return response;

  // All errors >400 throw errors
  const error: ApiResponseErrorType = new APIResponseError(response.statusText);
  error.response = response;
  throw error;
}

const parseJSON = res => res.json();
const parseBlob = res => res.blob();
// const parseText = res => res.text();

const endPoints = {
  currentUser: '/user/retrieveCurrentUser',
  newDesign: '/home',
  evaluate: '/evaluate',
  typelist: '/typeinfolist',
  allRecords: '/getRecordsByUser',
  singleRecord: '/getRecordById',
  saveRecord: '/save',

  updateRecord: '/updateRecordById/',
  records: '/records',
};

const getFullPath = path =>
  pathJoin([process.env.REACT_APP_API_BASE, process.env.REACT_APP_API_ROOT_PATH, path], '/');

export function fetchGet(path: string): Promise<any> {
  const fullUrl = getFullPath(path);

  return fetch(fullUrl, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
    .then(checkStatus)
    .then(response => response.json())
    .then(response => ({ response }));
}

// eslint-disable-next-line
export function fetchPost(path: string, data: any, handler: Function = parseJSON, override: boolean = false): Promise<any> {
  const fullUrl = override ? path : getFullPath(path);

  return fetch(fullUrl, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
    .then(checkStatus)
    .then(handler)
    .then(response => ({ response }));
}

export async function fetchByMethod(
  path: string,
  method: 'POST' | 'COPY' | 'PUT' | 'HEAD' | 'DELETE' | 'PATCH',
  payload: string = '',
) {
  const fullUrl = path.includes('design/') ? path : getFullPath(path);

  // the fetchPost function does not work with this new post API... wtf
  const rawResponse = await fetch(fullUrl, {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: payload,
  });
  return rawResponse.json().then(response => ({ response }));
}

// export const fetchParts: Function = () => fetchGet(`${API_ROOT}/product/all`);
export const fetchCurrentUser = () => fetchGet(endPoints.currentUser);
export const fetchDefaultData = () => fetchGet(endPoints.newDesign);
export const fetchTypeInfoList = () => fetchGet(endPoints.typelist);

// export const fetchDefaultData = () => fetchPost(
//   endPoints.newDesign, {
//     actionEvent: {
//       userNbr: '226612',
//     },
//   },
// );
// export const fetchDesignRecords = () => fetchGet(endPoints.allRecords);
// export const fetchDesignRecord = (id: string) => fetchGet(`${endPoints.singleRecord}/${id}`);
export const saveDesignRecord = (data: any) => fetchPost(endPoints.saveRecord, data);

export const fetchAllRecords = () => fetchGet(endPoints.records);
export const fetchRecord = (id: string) =>
  fetchGet(`${endPoints.singleRecord}/${id}`);

export const updateRecordName = async (payload: {
  id: number,
  name: string,
}) => {
  const path = pathJoin(
    [endPoints.updateRecord, payload.id, payload.name],
    '/',
  );
  return fetchByMethod(path, 'POST');
};

export const copyDesign = async (payload: { id: number, name: string }) => {
  const path = pathJoin([endPoints.records, payload.id, 'copy'], '/');
  return fetchByMethod(path, 'POST', JSON.stringify({ name: payload.name }));
};

export const updateDesign = async (payload: { data: any, id: number }) => {
  const path = pathJoin([endPoints.records, payload.id], '/');

  // hack - need to map the internet keys to the backend keys..
  const map = {
    IPhase: 'motorRmsPhsCurr',
    Tamb: 'ambientTmpt',
    RthCS: 'csHtsnkThrmlRest',
    Vbus: 'dcBusVolt',
    TSinkMax: 'maxHeatSinkTmpt',
    TjMax1: 'maxJunctnTmpt',
    MI: 'modulatnIdx',
    OutputFreq: 'outputFreq',
    OverLoadPercent: 'overloadPctg',
    PartNumber: 'partSelected',
    SpmPackage: 'pkg',
    PWM: 'pwmMethod',
    PF: 'pwrFctr',
    RthSA1: 'snkAmbThrmlRest',
    ManualRSink: 'snkAmbThrmlRestCalcMtd',
    SwitchingFreqKhz: 'switchingFreq',
  };

  let pl = mapKeys(payload.data, (v, k) => map[k]);
  pl = mapValues(pl, v => v.currentValue);
  return fetchByMethod(path, 'PUT', JSON.stringify(pl));
};

export const deleteDesign = async (id: number) => {
  const path = pathJoin([endPoints.records, id], '/');
  return fetchByMethod(path, 'DELETE');
};


// export const fetchChartData: Promise<*> = data => fetchPost(`${API_ROOT}/syncbuck/chartData`, data);
export const evaluateData = (data: any) => fetchPost(endPoints.evaluate, data);
// export const fetchSchematicData: Promise<*> = data => fetchPost(`${API_ROOT}/schematic/svg`, data, parseText);
export const downloadReportPDF = (data: any) => fetchPost(
  '/design/motioncontrol-api/v1/report/downloadPDF', data, parseBlob, true,
);

const origin = document.location.origin;
const redirect = `${process.env.REACT_APP_API_LOGIN}?referer=${origin}${process.env.REACT_APP_BASE_PATH}`;
export const loginRedirect = () => window.location.replace(redirect);



// WEBPACK FOOTER //
// ./src/services/api.js